import React, { useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import RouteIcon from '@mui/icons-material/Route'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { GX_BORDER, GX_INK_500, GX_INK_900, GX_COLORS } from '../growthos-shared/GrowthOsUi'
import {
  DataTable, ExpandableTable, GX_INK_400, MBR_TITLE, Muted, Neg, ScopeTag, compactCurrency, integer, percentage,
} from './pfUi'
import { TraceGapPanel } from './PfDealTable'

const ABM_LEAD_BASIS = 'ABM is counted on reply date (HubSpot "ABM Last Replied Date"), not contact create date — '
  + 'a contact counts in the period they replied to outreach. Every other source here is counted on create date. '
  + 'Sub-source is not filtered: the ABM tool is the only writer of the reply date, and Sub-source is unmaintained on imported records.'

function SourceLabel({ name }) {
  const label = name == null ? '—' : name
  if (String(name == null ? '' : name).trim().toLowerCase() !== 'abm') return <Box component="span" sx={{ fontWeight: 500 }}>{label}</Box>
  return (
    <Tooltip title={ABM_LEAD_BASIS} arrow>
      <Box component="span" sx={{ fontWeight: 500, cursor: 'help' }}>
        {label}
        <Box component="sup" sx={{ ml: 0.5, px: 0.5, borderRadius: '4px', fontSize: 9, fontWeight: 600, color: GX_COLORS.indigo, bgcolor: '#eef2ff' }}>reply-dated</Box>
      </Box>
    </Tooltip>
  )
}

const FLOW_STAGES = [
  { id: 1, tag: 'Stage 1', kicker: 'Lead Generation · context', title: 'Leads by Source' },
  { id: 2, tag: 'Stage 2', kicker: 'Deals · source + pipeline', title: 'Deals Created' },
  { id: 3, tag: 'Stage 3', kicker: 'Stage Reached · pipeline-wise', title: 'Ladder Progress' },
  { id: 4, tag: 'Stage 4', kicker: 'Won / WIP / Lost', title: 'Outcomes' },
]

const ACCENT_GRAD = 'linear-gradient(90deg,#4f46e5,#7c3aed,#06b6d4)'

function StageTile({ label, value, hint }) {
  return (
    <Box sx={{ borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.05)', px: 2, py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.5)' }}>
        {label}
        {hint && <Tooltip title={hint} arrow><InfoOutlinedIcon sx={{ fontSize: 12, cursor: 'help' }} /></Tooltip>}
      </Box>
      <Typography sx={{ mt: 0.5, fontSize: 24, fontWeight: 600, color: '#fff' }}>{value}</Typography>
    </Box>
  )
}

// One column set for the by-source and by-pipeline rows (they differ only in the label column).
function flowCols(label) {
  return [
    { key: 'label', header: label.header, title: label.title, cell: (r) => <Box component="span" sx={{ fontWeight: 500 }}>{label.value(r)}</Box>, sortValue: (r) => label.value(r) },
    { key: 'deals', header: 'Deals', align: 'right', title: 'Distinct deals — deduped by deal, because one deal can be associated with more than one cohort lead.', cell: (r) => integer(r.deals), sortValue: (r) => r.deals },
    { key: 'traceable', header: 'Lead-traceable', align: 'right', title: 'Deals in this row reachable back to a marketing-sourced lead through HubSpot deal↔contact associations. A data-quality share of the same deals, not a separate funnel stage.', cell: (r) => integer(r.leadTraceable), sortValue: (r) => r.leadTraceable },
    {
      key: 'gap', header: 'Gap', align: 'right',
      title: 'Deals in this row that CANNOT be traced back to a marketing lead. Click the row to list them with the specific fix each one needs and a link straight into HubSpot.',
      cell: (r) => { const gap = r.deals - r.leadTraceable; return gap ? <Box component="span" sx={{ fontWeight: 600, color: '#b45309' }}>{integer(gap)}</Box> : <Muted /> },
      sortValue: (r) => r.deals - r.leadTraceable,
    },
    { key: 'won', header: 'Won', align: 'right', title: 'Agreement Signed or Onboarding Completed.', cell: (r) => integer(r.won), sortValue: (r) => r.won },
    { key: 'wip', header: 'WIP', align: 'right', title: 'Still open on some rung.', cell: (r) => integer(r.wip), sortValue: (r) => r.wip },
    { key: 'lost', header: 'Lost', align: 'right', cell: (r) => (r.lost ? <Neg>{integer(r.lost)}</Neg> : '—'), sortValue: (r) => r.lost },
    { key: 'decided', header: 'Decided', align: 'right', title: 'Won + Lost — the deals that actually have an outcome. Shown so Closure %’s denominator is visible rather than implied.', cell: (r) => integer(r.decided), sortValue: (r) => r.decided },
    { key: 'win', header: 'Win %', align: 'right', title: 'Won ÷ ALL deals in this row, including those still in WIP. Reads low for a young cohort and rises as WIP resolves — the honest "what has this cohort delivered so far" number.', cell: (r) => percentage(r.winRatePct), sortValue: (r) => r.winRatePct },
    { key: 'closure', header: 'Closure %', align: 'right', title: 'Won ÷ (Won + Lost) — DECIDED deals only, WIP excluded. The app’s existing Closure % definition (see the Marketing dashboard): of the deals that reached an outcome, how many we won.', cell: (r) => percentage(r.closurePct), sortValue: (r) => r.closurePct },
    { key: 'mbr', header: 'Est. MBR', align: 'right', title: MBR_TITLE, cell: (r) => compactCurrency(r.estimatedMbr), sortValue: (r) => r.estimatedMbr },
  ]
}

const SRC_COLS = flowCols({
  header: 'Source (Deal Sub-Category)',
  title: "The canonical Deal Source Sub Category. Stage 1 uses the LEAD's sub-category instead.",
  value: (r) => r.subcategory,
})
const PIPE_COLS = flowCols({
  header: 'Pipeline',
  title: 'The pipeline the deal was created in. Pipeline does not exist at Stage 1 — a lead only acquires one by becoming a deal.',
  value: (r) => r.pipelineLabel,
})

const subLabel = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: GX_INK_500, mb: 1 }

export default function PipelineLeadFlow({ data }) {
  const [openStage, setOpenStage] = useState(1)
  const f = data.leadFlow
  const hub = data.hubspot
  const stage = openStage == null ? 1 : openStage
  const lt = f.stage3.ladderTotals || []
  const sub = (id) => (id === 1 ? `Leads generated: ${integer(f.stage1.leads)}`
    : id === 2 ? `Deals created: ${integer(f.stage2.dealsCreated)}`
      : id === 3 ? `Reached RFI Sent: ${integer(lt[0] ? lt[0].deals : 0)}`
        : `Won ${integer(f.stage4.won)} · WIP ${integer(f.stage4.wip)} · Lost ${integer(f.stage4.lost)}`)
  const cohortDeals = data.cohortDeals || []

  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden', borderRadius: '28px', border: '1px solid rgba(79,70,229,0.14)', p: 3, mb: 2.5,
      background: 'linear-gradient(160deg, #eef2ff 0%, #ffffff 55%, #f5f3ff 100%)',
      boxShadow: '0 1px 0 rgba(255,255,255,0.7) inset, 0 24px 48px -22px rgba(79,70,229,0.38), 0 8px 18px -10px rgba(15,23,42,0.12)',
      '&::before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 4, background: ACCENT_GRAD },
    }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 15, fontWeight: 700, color: GX_INK_900 }}>
          <RouteIcon sx={{ fontSize: 17, color: '#6366f1' }} />Lead → Deal → Stage → Outcome
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <ScopeTag label={`Same cohort as above: ${data.window.label}`} title={f.basisNote} />
          <ScopeTag label="Stage 1 is context, not a rung" title={f.stage1Note} />
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {FLOW_STAGES.map((s) => {
          const active = s.id === openStage
          return (
            <Box key={s.id} component="button" aria-expanded={active} onClick={() => setOpenStage(openStage === s.id ? null : s.id)}
              sx={{
                textAlign: 'left', cursor: 'pointer', p: 2, borderRadius: '16px', transition: 'all 200ms',
                border: active ? '1px solid transparent' : '1px solid rgba(15,23,42,0.06)',
                background: active ? 'linear-gradient(155deg, #1e1b4b 0%, #0b1220 65%)' : 'linear-gradient(160deg, #ffffff 0%, #f8fafc 100%)',
                boxShadow: active
                  ? 'inset 0 2px 10px rgba(0,0,0,0.45), 0 12px 28px -8px rgba(79,70,229,0.55), 0 0 0 1px rgba(129,140,248,0.35)'
                  : '0 1px 0 rgba(255,255,255,0.85) inset, 0 10px 20px -10px rgba(79,70,229,0.22), 0 2px 6px rgba(15,23,42,0.05)',
                '&:hover': active ? undefined : { transform: 'translateY(-2px)' },
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: active ? 'rgba(255,255,255,.55)' : GX_INK_400 }}>
                <Box component="span" sx={{
                  width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: '#fff', background: ACCENT_GRAD, boxShadow: '0 2px 6px rgba(79,70,229,0.5)',
                }}>{s.id}</Box>
                {s.tag}
              </Box>
              <Typography sx={{ mt: 0.5, fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: active ? 'rgba(255,255,255,.6)' : GX_INK_400 }}>{s.kicker}</Typography>
              <Typography sx={{ mt: 0.25, fontSize: 18, fontWeight: 600, color: active ? '#fff' : '#1e293b' }}>{s.title}</Typography>
              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, fontSize: 12, color: active ? 'rgba(255,255,255,.7)' : GX_INK_500 }}>
                <span>{sub(s.id)}</span>
                <ExpandMoreIcon sx={{ fontSize: 16, transform: active ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
              </Box>
            </Box>
          )
        })}
      </Box>

      {openStage !== null && (
        <Box sx={{
          position: 'relative', overflow: 'hidden', borderRadius: '24px',
          background: 'linear-gradient(155deg, #161b33 0%, #0b1220 65%)',
          boxShadow: '0 24px 48px -16px rgba(79,70,229,0.45), 0 8px 20px -8px rgba(15,23,42,0.4)',
          '&::before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 3, background: ACCENT_GRAD },
        }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, p: 2.5 }}>
            <Box sx={{ mr: 'auto' }}>
              <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.45)' }}>{FLOW_STAGES[stage - 1].kicker}</Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>{FLOW_STAGES[stage - 1].title}</Typography>
            </Box>
            {stage === 1 && (
              <StageTile label="Marketing Leads" value={integer(f.stage1.leads)} hint="Marketing-sourced contacts created in the selected window, with their source mix. CONTEXT for the stages that follow, not the funnel's first rung: leads are contacts, stages 2-4 count deals, and one lead can become several deals — so a lead→deal ratio would cross grain and can rise rather than fall." />
            )}
            {stage === 2 && (
              <>
                <StageTile label="Deals Created" value={integer(f.stage2.dealsCreated)} hint="The SAME number as the Deals Created KPI at the top of the page and the same cohort as the Canonical Stage Ladder — deals created in the selected window." />
                <StageTile label="Est. MBR" value={compactCurrency(f.stage2.estimatedMbr)} hint={MBR_TITLE} />
                <StageTile label="Lead-traceable" value={integer(f.stage2.leadTraceable)} hint={f.traceabilityNote} />
              </>
            )}
            {stage === 3 && (
              <>
                <StageTile label="Reached RFI Sent" value={integer(lt[0] ? lt[0].deals : 0)} hint={`Cumulative "ever reached", from stage-entry history — not where deals sit now. Can read just below Deals Created because ${integer(f.stage3.noRung)} deal(s) in this cohort never occupied a live rung (their only stage event is Closed Lost).`} />
                <StageTile label="Reached Commercial" value={integer(lt[2] ? lt[2].deals : 0)} />
                <StageTile label="Reached Onboarded" value={integer(lt[6] ? lt[6].deals : 0)} />
              </>
            )}
            {stage === 4 && (
              <>
                <StageTile label="Won" value={integer(f.stage4.won)} hint="Agreement Signed or Onboarding Completed." />
                <StageTile label="WIP" value={integer(f.stage4.wip)} hint="Still open on some rung." />
                <StageTile label="Lost" value={integer(f.stage4.lost)} />
                <StageTile label="Win %" value={percentage(f.stage4.winRatePct)} hint="Won ÷ all deals in the cohort — WIP counts in the denominator." />
                <StageTile label="Closure %" value={percentage(f.stage4.closurePct)} hint={`Won ÷ (Won + Lost) — decided deals only (${integer(f.stage4.decided)} of ${integer(f.stage2.dealsCreated)}). The app's existing Closure % definition.`} />
              </>
            )}
          </Box>

          {/* White surface under the tables — the table ink is built for a light background. */}
          <Box sx={{ overflowX: 'auto', borderRadius: '0 0 24px 24px', bgcolor: '#fff', px: 1.5, py: 2, borderTop: `1px solid ${GX_BORDER}` }}>
            {stage === 1 && (
              <DataTable
                rows={f.stage1.bySource} paginate={false} rowKey={(r) => r.subcategory}
                columns={[
                  { key: 'src', header: 'Source (Lead Sub-Category)', title: "The LEAD's own source sub-category. Stages 2-4 use the DEAL's own source, which is the canonical taxonomy used everywhere else in the app.", cell: (r) => <SourceLabel name={r.subcategory} />, sortValue: (r) => r.subcategory },
                  { key: 'leads', header: 'Leads', align: 'right', title: 'Marketing-sourced contacts created in the window from this source. EXCEPTION: the ABM row is counted on reply date, not create date — hover the ABM label.', cell: (r) => integer(r.leads), sortValue: (r) => r.leads },
                ]}
              />
            )}
            {(stage === 2 || stage === 4) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography sx={subLabel}>By pipeline</Typography>
                  <ExpandableTable
                    rows={f.stage2.byPipeline} paginate={false} rowKey={(r) => r.pipelineLabel} columns={PIPE_COLS}
                    renderExpanded={(r) => (
                      <TraceGapPanel label={r.pipelineLabel} hub={hub}
                        rows={cohortDeals.filter((d) => !d.leadTraceable && d.pipelineLabel === r.pipelineLabel)} />
                    )}
                  />
                </Box>
                <Box>
                  <Typography sx={subLabel}>By source</Typography>
                  <ExpandableTable
                    rows={f.stage2.bySource} paginate={false} rowKey={(r) => r.subcategory} columns={SRC_COLS}
                    renderExpanded={(r) => (
                      <TraceGapPanel label={r.subcategory} hub={hub}
                        rows={cohortDeals.filter((d) => !d.leadTraceable && d.subcategory === r.subcategory)} />
                    )}
                  />
                </Box>
              </Box>
            )}
            {stage === 3 && (
              <DataTable
                rows={f.stage3.byPipeline} paginate={false} rowKey={(r) => r.pipelineLabel}
                columns={[
                  { key: 'pipe', header: 'Pipeline', cell: (r) => <Box component="span" sx={{ fontWeight: 500 }}>{r.pipelineLabel}</Box>, sortValue: (r) => r.pipelineLabel },
                  ...data.ladder.map((l) => ({
                    key: `s${l.step}`, header: l.label, align: 'right',
                    title: `Deals in this pipeline that ever reached "${l.label}". Cumulative.`,
                    cell: (r) => (r.cells && r.cells[String(l.step)] ? integer(r.cells[String(l.step)]) : <Muted />),
                    sortValue: (r) => (r.cells && r.cells[String(l.step)]) || 0,
                  })),
                  { key: 'won', header: 'Won', align: 'right', cell: (r) => integer(r.won), sortValue: (r) => r.won },
                  { key: 'wip', header: 'WIP', align: 'right', cell: (r) => integer(r.wip), sortValue: (r) => r.wip },
                  { key: 'lost', header: 'Lost', align: 'right', cell: (r) => (r.lost ? <Neg>{integer(r.lost)}</Neg> : '—'), sortValue: (r) => r.lost },
                ]}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
